import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Button } from 'components/Button';
import { FilterControls } from 'components/FiltersControls';
import { Heading } from 'components/Heading';
import { ModalButton } from 'components/ModalButton';
import { Table } from 'components/Table';
import { TinySkinPreview } from 'components/TinySkinPreview';
import { emitMessage } from 'engine/api';
import { appConfig, isRouteAllowed } from 'engine/appConfig';
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { GameTypeOptions } from 'shared/modelTypes/game';

export const Route = createFileRoute('/dashboard/variants/')({
	component: RouteComponent,
	loader: async () => {
		const data = await emitMessage({ kind: 'get-variant-list' });
		return {
			variants: data.variantViews ?? [],
			games: data.gameViews ?? [],
			gameMap: data.gameMap,
			languages: data.languages ?? [],
			// skins: data.,
		};
	},
	beforeLoad: () => {
		isRouteAllowed('allowVariants');
	},
});

function RouteComponent() {
	const data = Route.useLoaderData();
	const router = useRouter();

	const [variants, setVariants] = useState(data.variants);

	return (
		<div className="flex flex-col gap-3">
			<div className="border-yellow flex items-center gap-3">
				<Heading>Variants</Heading>
				<Button to="/dashboard/variants/new">
					New Variant <Plus className="size-4" />
				</Button>
			</div>

			<FilterControls
				items={data.variants || []}
				getSearchableText={(s) => s.name}
				onChanged={setVariants}
				filterOptions={[
					{
						key: 'game',
						label: 'Game',
						values: (data?.games?.sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })) || []).map(
							(g) => ({ label: g.name, value: g.id }),
						),
						getValue: (variant) => variant.gameId,
					},
					{
						key: 'type',
						label: 'Type',
						values: GameTypeOptions.map((x) => ({ value: x.label, label: x.label })),
						getValue: (game) => game.type ?? '',
					},
					{
						key: 'lang',
						label: 'lang',
						values: data.languages.map((language) => ({
							label: `${language}`,
							value: language,
						})),
						getValue: (variant) => {
							const game = data?.gameMap?.[variant.gameId];

							return game?.language || '';
						},
					},
				]}
				sortOptions={[
					{
						key: 'name',
						label: 'Name',
						sortFn: (a, b) => a.name.localeCompare(b.name),
					},
					{
						key: 'createdAt',
						label: 'Creation date',
						sortFn: (a, b) => a.createdAt.localeCompare(b.createdAt),
					},
					{
						key: 'updatedAt',
						label: 'Last updated',
						sortFn: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
					},
				]}
			/>

			{!data.variants && <span>Could not get variants.</span>}
			{variants && (
				<Table
					className="w-fit"
					headers={['Name', 'Game', 'Type', 'Lang', appConfig.permissions.allowSkins ? 'Skin' : '', 'Actions']}
					rows={variants!.map((v) => {
						return [
							// ----------- NAME -----------
							<span className="font-bold">{v.name}</span>,
							// ----------- GAME -----------
							<span>{v.gameName}</span>,
							// ----------- TYPE -----------
							<span>{v.type}</span>,
							// ---------- Language ----------
							<span className="text-md size-full justify-center text-center align-middle">
								<img className="size-4" src={`/flags/${v.language}.svg`} />
							</span>,
							// ----------- SKIN -----------
							appConfig.permissions.allowSkins ? <TinySkinPreview skin={v.skin?.skin} /> : <></>,
							// ----------- ACTIONS -----------
							<div className="flex gap-1">
								<Button to={'/dashboard/variants/edit/$id'} params={{ id: v.id }} mode={'primary'} className="p-1">
									<Pencil />
								</Button>
								<ModalButton
									title={`Make a copy of "${v.name}"?`}
									onAccept={async () => {
										await emitMessage({ kind: 'variant-copy', id: v.id });
										router.navigate({ to: '/dashboard/variants' });
									}}
									mode={'secondary'}
									className="border-2 border-gray-200 p-1"
								>
									<Copy />
								</ModalButton>
								<ModalButton
									title={`Are you sure you want to delete "${v.name}"?`}
									onAccept={async () => {
										await emitMessage({ kind: 'variant-delete', id: v.id });
										router.navigate({ to: '/dashboard/variants' });
									}}
									mode={'secondary'}
									className="border-2 border-gray-200 p-1"
								>
									<Trash2 />
								</ModalButton>
							</div>,
						];
					})}
				></Table>
			)}
		</div>
	);
}
